/**
 * Relay/UI status derivation (mobile-only, pure).
 *
 * The PWA used to derive its relay copy from a single boolean
 * (`network.relayConnected`) and kept the last readiness message in
 * `network.detail` forever. Two consequences after a daemon restart or a page
 * refresh:
 *
 * - a transient "密钥尚未就绪"/"列表请求失败" message stayed on screen after the
 *   relay had already recovered (stale “未就绪”);
 * - "未配置 live relay" / "连接中" / "已连接" were not distinguishable once the
 *   boolean flipped.
 *
 * These helpers make the transition explicit and testable: socket statuses are
 * folded into `NetworkState` (`relayStatus` + `relayConnected`), readiness hints
 * live in `relayDetail` and are cleared as soon as the channel is usable.
 */
import { relayStatusLabel, type RelayStatus } from '../api/relay';
import type { NetworkState } from '../types';
import type { LiveRelayPlan } from './plan';

/**
 * Fold a relay socket status into the network state.
 *
 * The socket lifecycle hint (disconnected / error / handshake timeout) lives in
 * `relayDetail`, not `detail`: it is replaced or cleared by the next status
 * change, so a recovered connection never keeps showing “连接已断开”. A
 * successful connection also drops any readiness hint (“未就绪”) that was set
 * while the controller/key was still coming up.
 */
export function networkAfterRelayStatus(
  previous: NetworkState,
  status: RelayStatus,
  detail?: string,
): NetworkState {
  const relayConnected = status === 'connected';
  // connected/idle/connecting all mean "no lingering failure copy".
  const clearLifecycle = relayConnected || status === 'connecting' || status === 'idle';
  const lifecycleHint = clearLifecycle ? undefined : (detail ?? previous.relayDetail);
  return {
    ...previous,
    relayStatus: status,
    relayConnected,
    relayDetail: lifecycleHint,
  };
}

/** A successful daemon response proves the channel works: clear stale hints. */
export function networkAfterRelaySync(previous: NetworkState, now: number): NetworkState {
  return { ...previous, relayDetail: undefined, lastSyncAt: now };
}

/** Set the relay readiness hint (never the transient operational `detail`). */
export function withRelayDetail(previous: NetworkState, message: string | undefined): NetworkState {
  if (previous.relayDetail === message) return previous;
  return { ...previous, relayDetail: message };
}

export type RelayPhase =
  | 'demo'
  | 'unpaired'
  | 'unconfigured'
  | 'insecure'
  | 'deriving'
  | 'connecting'
  | 'connected'
  | 'offline'
  | 'error';

export type RelayTone = 'ok' | 'busy' | 'warn' | 'danger' | 'idle';

export interface RelayStatusView {
  phase: RelayPhase;
  /** Short user-facing label (existing copy is preserved). */
  label: string;
  /** E2E control channel usable right now. */
  ready: boolean;
  tone: RelayTone;
}

export interface RelayStatusViewInput {
  demoMode: boolean;
  paired: boolean;
  plan: Pick<LiveRelayPlan, 'enabled' | 'configured' | 'secure'>;
  relayConnected: boolean;
  relayStatus?: RelayStatus;
  /** Derived E2E channel key is available (live mode only). */
  keyReady: boolean;
}

/**
 * Compute the banner copy. Order matters: configuration gaps are reported
 * before connection state so "not configured" is never rendered as
 * "disconnected", and a configured-but-insecure context keeps its HTTPS copy.
 */
export function relayStatusView(input: RelayStatusViewInput): RelayStatusView {
  if (input.demoMode) {
    return { phase: 'demo', label: '演示 daemon', ready: false, tone: 'idle' };
  }
  if (!input.paired) {
    return { phase: 'unpaired', label: '未配对', ready: false, tone: 'danger' };
  }
  if (!input.plan.enabled) {
    if (input.plan.configured && !input.plan.secure) {
      return { phase: 'insecure', label: '需要 HTTPS（E2E）', ready: false, tone: 'warn' };
    }
    return { phase: 'unconfigured', label: '未配置 live relay', ready: false, tone: 'warn' };
  }
  if (!input.keyReady) {
    // Config is complete but the channel key is still being derived.
    return { phase: 'deriving', label: relayStatusLabel('connecting'), ready: false, tone: 'busy' };
  }
  if (input.relayConnected) {
    return {
      phase: 'connected',
      label: relayStatusLabel('connected'),
      ready: true,
      tone: 'ok',
    };
  }
  switch (input.relayStatus) {
    case 'offline':
      return {
        phase: 'offline',
        label: relayStatusLabel('offline'),
        ready: false,
        tone: 'warn',
      };
    case 'error':
      return { phase: 'error', label: relayStatusLabel('error'), ready: false, tone: 'danger' };
    case 'connecting':
    default:
      return {
        phase: 'connecting',
        label: relayStatusLabel('connecting'),
        ready: false,
        tone: 'busy',
      };
  }
}

/** Connection states where an explicit (user/network) retry is meaningful. */
export function relayPhaseIsRetryable(phase: RelayPhase): boolean {
  return phase === 'offline' || phase === 'error';
}
